/**
 * テレメトリの公開 API（docs/spec/observability.md §5、§6、§11）。
 *
 * 無効時（dev / preview / テスト / 本番以外のホスト / 'off'）は Faro を起動しない。
 * そのとき withSpan / tracedFetch は @opentelemetry/api の no-op 実装で動き、何も送らない。
 */
import {
  context,
  isSpanContextValid,
  SpanStatusCode,
  trace,
  TraceFlags,
  type Span,
} from '@opentelemetry/api';
import type { Faro } from '@grafana/faro-web-sdk';
import {
  sanitizeAttributes,
  type CardKind,
  type ClassAttributes,
  type GradeMethod,
  type RunOutcome,
  type SpanName,
  type Verdict,
} from './attributes';
import {
  decideTelemetry,
  environmentName,
  STORAGE_KEY,
  type Decision,
  type TelemetryEnv,
} from './enablement';
import { startFaro } from './faro';

export type {
  CardKind,
  ClassAttributeKey,
  ClassAttributes,
  GradeMethod,
  RunOutcome,
  RunPurpose,
  SpanName,
  Verdict,
} from './attributes';
export { CLASS_ATTRIBUTE_KEYS, CLASS_ATTRIBUTE_NAME } from './attributes';
export type { Span } from '@opentelemetry/api';

const TRACER_NAME = 'inductor-class';
const RECENT_LIMIT = 10;

let faro: Faro | null = null;
let status: Decision | null = null;
const recent: { name: SpanName; traceId: string }[] = [];
/** 開いている class.run / class.fmt span（内側が末尾）。/api/* は同時 1 件なので曖昧にならない。 */
const openRunSpans: Span[] = [];
const RUN_SPAN_NAMES: ReadonlySet<SpanName> = new Set<SpanName>(['class.run', 'class.fmt']);

const tracer = () => trace.getTracer(TRACER_NAME);

export interface InitTelemetryOptions {
  /** 本番ホスト以外でも起動する（'off' 指定には負ける）。 */
  force?: boolean;
  /** 既定は import.meta.env と location / localStorage から読む。テストで差し替える。 */
  env?: Partial<TelemetryEnv>;
  /** 既定は VITE_APP_VERSION（ビルド時の git sha）。 */
  version?: string;
}

export interface TelemetryStatus {
  readonly enabled: boolean;
  readonly reason: Decision['reason'];
}

function readStorageFlag(): string | null {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    // Safari のプライベートブラウズなどで localStorage が使えない。
    return null;
  }
}

function readEnv(): TelemetryEnv {
  const viteTelemetry: unknown = import.meta.env['VITE_TELEMETRY'];
  return {
    prod: import.meta.env.PROD,
    dev: import.meta.env.DEV,
    origin: globalThis.location?.origin ?? '',
    viteTelemetry: typeof viteTelemetry === 'string' ? viteTelemetry : undefined,
    storageFlag: readStorageFlag(),
  };
}

function readVersion(): string {
  const v: unknown = import.meta.env['VITE_APP_VERSION'];
  return typeof v === 'string' && v !== '' ? v : 'dev';
}

/**
 * 本番ホストでだけ Faro を起動する。二度呼んでも 1 回だけ。
 * `src/main.ts` でアプリを mount する前に呼ぶ。
 */
export function initTelemetry(opts: InitTelemetryOptions = {}): TelemetryStatus {
  if (status) return status;
  const env: TelemetryEnv = { ...readEnv(), ...opts.env };
  const decision = decideTelemetry(env, opts.force ?? false);
  status = decision;
  Object.defineProperty(globalThis, '__classTelemetry', {
    value: Object.freeze({ recentTraceIds }),
    configurable: true,
  });
  if (!decision.enabled) return decision;
  try {
    faro = startFaro({
      origin: env.origin,
      version: opts.version ?? readVersion(),
      environment: environmentName(env),
    });
  } catch {
    // 計測の失敗でアプリを止めない。
    faro = null;
    status = { enabled: false, reason: 'not-production' };
  }
  return status;
}

export function telemetryEnabled(): boolean {
  return faro !== null;
}

function remember(name: SpanName, span: Span): void {
  const { traceId } = span.spanContext();
  if (!isSpanContextValid(span.spanContext())) return; // no-op span（無効時）は記録しない
  recent.unshift({ name, traceId });
  recent.length = Math.min(recent.length, RECENT_LIMIT);
}

/** 動作確認用。直近の withSpan の trace id（新しい順、最大 10 件）。 */
export function recentTraceIds(): readonly { name: SpanName; traceId: string }[] {
  return recent.map((r) => ({ ...r }));
}

async function runInSpan<T>(
  span: Span,
  name: SpanName,
  fn: (span: Span) => Promise<T> | T,
): Promise<T> {
  remember(name, span);
  const isRun = RUN_SPAN_NAMES.has(name);
  if (isRun) openRunSpans.push(span);
  try {
    // fn の同期部分だけが span を active にした context で走る（StackContextManager）。
    return await context.with(trace.setSpan(context.active(), span), () => fn(span));
  } catch (e) {
    span.recordException(e instanceof Error ? e : new Error(String(e)));
    span.setStatus({ code: SpanStatusCode.ERROR });
    throw e;
  } finally {
    if (isRun) openRunSpans.splice(openRunSpans.lastIndexOf(span), 1);
    span.end();
  }
}

/**
 * span を作り、fn の完了（Promise の解決）で閉じる。親は「いま active な span」。
 * fn が throw / reject したら recordException + status=ERROR にして投げ直す。
 */
export function withSpan<T>(
  name: SpanName,
  attributes: ClassAttributes,
  fn: (span: Span) => Promise<T> | T,
): Promise<T> {
  const span = tracer().startSpan(name, { attributes: sanitizeAttributes(attributes) });
  return runInSpan(span, name, fn);
}

/** parent を明示的に親にして span を始める。await の後でも親子関係を保てる。 */
export function withSpanUnder<T>(
  parent: Span | undefined,
  name: SpanName,
  attributes: ClassAttributes,
  fn: (span: Span) => Promise<T> | T,
): Promise<T> {
  const ctx = parent ? trace.setSpan(context.active(), parent) : context.active();
  const span = tracer().startSpan(name, { attributes: sanitizeAttributes(attributes) }, ctx);
  return runInSpan(span, name, fn);
}

/**
 * いま開いている `class.run` / `class.fmt` span（無ければ undefined）。
 * sandboxd クライアントは `await` の後で fetch を呼ぶので active context が切れる。
 * `deps.fetch` に `(i, init) => tracedFetch(i, init, currentRunSpan())` を渡して親を明示する
 * （sandboxd-client.md §4.7）。
 */
export function currentRunSpan(): Span | undefined {
  return openRunSpans.at(-1);
}

/**
 * span の W3C traceparent ヘッダ。sandboxd クライアントの headers フックに渡せる。
 * 無効な span（テレメトリ無効時の no-op）と未サンプルの span では空にする（C6: `-00` を送らない）。
 */
export function traceHeaders(span: Span | undefined): Record<string, string> {
  if (!span) return {};
  const sc = span.spanContext();
  if (!isSpanContextValid(sc) || (sc.traceFlags & TraceFlags.SAMPLED) === 0) return {};
  return { traceparent: `00-${sc.traceId}-${sc.spanId}-01` };
}

function isSameOriginPath(input: string): boolean {
  return input.startsWith('/') && !input.startsWith('//');
}

/**
 * parent を active にした context の中で fetch を**同期的に呼ぶ**（C3）。
 * fetch instrumentation の CLIENT span が parent の子になり、その traceparent が付く。
 * instrumentation が無い環境でも parent の traceparent を付ける（instrumentation が上書きする）。
 * 他オリジンへ traceparent を出さないため、input は同一オリジンのパスだけを受け付ける。
 */
export function tracedFetch(
  input: string,
  init: RequestInit = {},
  parent?: Span,
): Promise<Response> {
  if (!isSameOriginPath(input)) {
    return Promise.reject(new TypeError('tracedFetch は同一オリジンのパスだけを受け付けます'));
  }
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries(traceHeaders(parent))) headers.set(k, v);
  const ctx = parent ? trace.setSpan(context.active(), parent) : context.active();
  return context.with(ctx, () => fetch(input, { ...init, headers }));
}

function pushEvent(name: string, attributes: Record<string, string>, span: Span): void {
  if (!faro) return;
  const sc = span.spanContext();
  const options = isSpanContextValid(sc)
    ? { spanContext: { traceId: sc.traceId, spanId: sc.spanId } }
    : {};
  faro.api.pushEvent(name, attributes, undefined, options);
}

export interface VerdictRecord {
  cardId: string;
  cardKind: CardKind;
  method: GradeMethod;
  verdict: Verdict;
}

/**
 * 判定の記録（§6.2）。span 属性 `class.verdict` とイベント `class.card.graded` を同じ値で出す。
 * 判定から導いた学習状態（正答率など）は受け取らない。
 */
export function recordVerdict(span: Span, record: VerdictRecord): void {
  span.setAttribute('class.verdict', record.verdict);
  pushEvent(
    'class.card.graded',
    {
      card_id: record.cardId,
      card_kind: record.cardKind,
      grade_method: record.method,
      verdict: record.verdict,
    },
    span,
  );
}

const FAILURE_OUTCOMES: ReadonlySet<RunOutcome> = new Set<RunOutcome>([
  'rate_limited',
  'busy',
  'gateway_timeout',
  'http_error',
  'network_error',
]);

/**
 * `class.run` / `class.fmt` の結果（§6.1）。completed なら verdict を付ける。
 * 基盤側の失敗は status=ERROR、`error.type`、イベント `class.run.failed` にする。
 */
export function recordRunOutcome(span: Span, outcome: RunOutcome, verdict?: Verdict): void {
  span.setAttribute('class.run.outcome', outcome);
  if (outcome === 'completed' && verdict) span.setAttribute('class.verdict', verdict);
  if (FAILURE_OUTCOMES.has(outcome)) {
    span.setStatus({ code: SpanStatusCode.ERROR });
    span.setAttribute('error.type', outcome);
    pushEvent('class.run.failed', { outcome }, span);
  }
}

/**
 * ページ遷移を Faro の view として記録し、`class.view.change` span で settle の完了までを測る。
 * route にはテンプレート（'/cards/:id'）を渡す。カード ID を入れない。
 */
export function trackView(route: string, settle?: () => Promise<void> | void): Promise<void> {
  faro?.api.setView({ name: route });
  return withSpan('class.view.change', { 'class.view.route': route }, async () => {
    await settle?.();
  });
}

/**
 * セッションに class.* 属性を足す（許可リスト外は落とす）。既存の属性（isSampled など）は保つ。
 * ユーザーを識別する値は受け取らない（setUser は呼ばない）。
 */
export function setSessionAttributes(attrs: ClassAttributes): void {
  if (!faro) return;
  const additions: Record<string, string> = {};
  for (const [k, v] of Object.entries(sanitizeAttributes(attrs))) additions[k] = String(v);
  if (Object.keys(additions).length === 0) return;
  const session = faro.api.getSession();
  faro.api.setSession({ ...session, attributes: { ...session?.attributes, ...additions } });
}
